import { createRouter, createWebHashHistory, RouteRecordRaw } from 'vue-router'
import { authService } from '../services/auth'
import { useUserStore } from '../stores/user-store'

import AuthLayout from '../layouts/AuthLayout.vue'
import AppLayout from '../layouts/AppLayout.vue'

import RouteViewComponent from '../layouts/RouterBypass.vue'

const routes: Array<RouteRecordRaw> = [
  {
    path: '/auth',
    component: AuthLayout,
    meta: { requiresAuth: false },
    children: [
      {
        name: 'login',
        path: 'login',
        component: () => import('../pages/auth/Login.vue'),
      },
      {
        name: 'signup',
        path: 'signup',
        component: () => import('../pages/auth/Signup.vue'),
      },
      {
        name: 'recover-password',
        path: 'recover-password',
        component: () => import('../pages/auth/RecoverPassword.vue'),
      },
      {
        name: 'recover-password-email',
        path: 'recover-password-email',
        component: () => import('../pages/auth/CheckTheEmail.vue'),
      },
      {
        path: '',
        redirect: { name: 'login' },
      },
    ],
  },
  {
    name: 'admin',
    path: '/',
    component: AppLayout,
    meta: { requiresAuth: true },
    children: [
      {
        name: 'new-dashboard',
        path: '',
        component: () => import('../modules/user/pages/Dashboard.vue'),
      },
      {
        name: 'admin-dashboard',
        path: 'admin-dashboard',
        component: () => import('../pages/admin/dashboard/AdminDashboard.vue'),
      },
      {
        name: 'super-dashboard',
        path: 'super-dashboard',
        component: () => import('../pages/admin/dashboard/AdminDashboard.vue'),
      },
      {
        name: 'settings',
        path: 'settings',
        component: () => import('../pages/settings/Settings.vue'),
      },
      {
        name: 'preferences',
        path: 'preferences',
        component: () => import('../modules/user/pages/Preferences.vue'),
      },
      {
        name: 'users',
        path: 'users',
        component: () => import('../modules/admin/pages/Users.vue'),
      },
      {
        name: 'projects',
        path: 'projects',
        component: () => import('../pages/projects/ProjectsPage.vue'),
      },
      {
        name: 'apps',
        path: 'apps',
        component: () => import('../modules/user/pages/Apps.vue'),
      },
      {
        name: 'modules',
        path: 'modules',
        component: () => import('../pages/modules/ModulesPage.vue'),
      },
      {
        name: 'admin-apps',
        path: 'admin-apps',
        component: () => import('../modules/admin/pages/AdminApps.vue'),
      },
      {
        name: 'admin-config',
        path: 'admin-config',
        component: () => import('../modules/admin/pages/Config.vue'),
      },
      {
        name: 'admin-versions',
        path: 'admin-versions',
        component: () => import('../modules/admin/pages/Versions.vue'),
      },
      {
        name: 'admin-features',
        path: 'admin-features',
        component: () => import('../modules/admin/pages/Features.vue'),
      },
      {
        name: 'admin-logs',
        path: 'admin-logs',
        component: () => import('../modules/admin/pages/Logs.vue'),
      },
      {
        name: 'admin-orders',
        path: 'admin-orders',
        component: () => import('../modules/admin/pages/Orders.vue'),
      },
      {
        name: 'admin-exceptions',
        path: 'admin-exceptions',
        component: () => import('../modules/admin/pages/Exceptions.vue'),
      },
      {
        name: 'super-users',
        path: 'super-users',
        component: () => import('../modules/super/pages/Users.vue'),
      },
      {
        name: 'super-subscription',
        path: 'super-subscription',
        component: () => import('../pages/admin/subscription/SubscriptionManagePage.vue'),
      },
      {
        name: 'payments',
        path: 'payments',
        component: RouteViewComponent,
        children: [
          {
            name: 'payment-methods',
            path: 'payment-methods',
            component: () => import('../pages/payments/PaymentsPage.vue'),
          },
          {
            name: 'billing',
            path: 'billing',
            component: () => import('../pages/billing/BillingPage.vue'),
          },
          {
            name: 'pricing-plans',
            path: 'pricing-plans',
            component: () => import('../pages/pricing-plans/PricingPlans.vue'),
          },
        ],
      },
      {
        name: 'faq',
        path: 'faq',
        component: () => import('../pages/faq/FaqPage.vue'),
      },
      {
        name: 'order',
        path: 'order',
        component: () => import('../pages/order/OrderPage.vue'),
      },
      {
        name: 'order-history',
        path: 'order-history',
        component: () => import('../pages/order/OrderHistoryPage.vue'),
      },
    ],
  },
  {
    name: '404',
    path: '/404',
    component: () => import('../pages/404.vue'),
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/404',
  },
]

const router = createRouter({
  history: createWebHashHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    // For some reason using documentation example doesn't scroll on page navigation.
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    } else {
      window.scrollTo(0, 0)
    }
  },
  routes,
})

// 路由守卫
router.beforeEach((to, from, next) => {
  const isLoggedIn = authService.isLoggedIn()
  
  // 如果用户未登录且访问需要认证的页面，重定向到登录页
  if (!isLoggedIn && to.meta.requiresAuth === true) {
    next({ name: 'login', replace: true })
    return
  }
  
  // 登录后：根路径或进入仪表盘时，按角色跳转到对应的仪表盘
  if (isLoggedIn) {
    const userStore = useUserStore()
    // 初始化用户状态（从本地缓存恢复）
    if (!userStore.userInfo) {
      userStore.initUserState()
    }
    const role = userStore.currentRole
    const targetName = role === 'superAdmin' ? 'super-dashboard' : role === 'admin' ? 'admin-dashboard' : 'new-dashboard'

    const isDashboardRoute = ['new-dashboard', 'admin-dashboard', 'super-dashboard'].includes(String(to.name || ''))
    if (to.path === '/' || isDashboardRoute) {
      if (to.name !== targetName) {
        next({ name: targetName, replace: true })
        return
      }
    }
  }
  
  // 其他情况正常通过
  next()
})

export default router
export { router }
